import { Group, SecurityClearance } from "@fairfleet/geotab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { buildClearanceSetEntity, isProtectedClearanceId } from "../clearanceHelpers";
import { useGeotabClient } from "@/components/GeotabClientContext";

export interface SaveClearanceVariables {
  /** The clearance being edited, or `null` when creating a new one. */
  existing: { id: string } | null;
  name: string;
  comment: string;
  parentId: string;
}

export function useClearanceMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { geotabClient, activeSessionId } = useGeotabClient();

  const invalidate = () =>
    Promise.all(
      ["securityClearances", "groups", "group", "usersBySearch"].map(key =>
        queryClient.invalidateQueries({ queryKey: [key, activeSessionId] }),
      ),
    );

  const save = useMutation({
    mutationFn: async ({ existing, name, comment, parentId }: SaveClearanceVariables) => {
      if (!name.trim()) throw new Error(t("clearances.validationName"));

      if (existing) {
        if (isProtectedClearanceId(existing.id)) throw new Error(t("clearances.protectedHint"));

        // Set replaces the whole entity, so work from the group as it is right now; a cached copy
        // would revert security-filter edits made in MyGeotab in the meantime.
        const [fresh] = (await geotabClient.get("Group", { id: existing.id }, 1)) as SecurityClearance[];
        if (fresh?.id !== existing.id) throw new Error(t("clearances.filters.loadError"));

        await geotabClient.set("Group", buildClearanceSetEntity(fresh, name, comment));
        return existing.id;
      }

      const createdId = await geotabClient.add("Group", {
        name: name.trim(),
        comments: comment,
        parent: { id: parentId } as Group,
      });
      return typeof createdId === "string" ? createdId : null;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      if (isProtectedClearanceId(id)) throw new Error(t("clearances.protectedHint"));
      await geotabClient.remove("Group", { id });
    },
    onSuccess: invalidate,
  });

  return { save, remove };
}
